import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Loader2, MapPin, Unlink, AlertCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Modal: pick a GBP account → location → link to the given client.
 * If the client already has gbp_location_id, shows "Unlink" option too.
 */
export default function GBPLinkDialog({ open, onOpenChange, client, onLinked }) {
  const [accounts, setAccounts] = useState([]);
  const [locations, setLocations] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState('');
  const [selectedLocation, setSelectedLocation] = useState('');
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [saving, setSaving] = useState(false);
  const [notConnected, setNotConnected] = useState(false);
  const [apiError, setApiError] = useState('');

  useEffect(() => {
    if (!open) return;
    setAccounts([]); setLocations([]); setSelectedAccount(''); setSelectedLocation('');
    setNotConnected(false); setApiError('');
    setLoadingAccounts(true);
    axios.get(`${API}/google-business/accounts`, { withCredentials: true })
      .then(({ data }) => {
        if (!data.connected) setNotConnected(true);
        if (data.error) setApiError(data.error);
        setAccounts(data.accounts || []);
      })
      .catch(e => toast.error(e.response?.data?.detail || 'Erreur'))
      .finally(() => setLoadingAccounts(false));
  }, [open]);

  useEffect(() => {
    if (!selectedAccount) return;
    setLocations([]); setSelectedLocation(''); setApiError('');
    setLoadingLocations(true);
    axios.get(`${API}/google-business/locations?account_id=${encodeURIComponent(selectedAccount)}`, { withCredentials: true })
      .then(({ data }) => {
        if (data.error) setApiError(data.error);
        setLocations(data.locations || []);
      })
      .catch(e => toast.error(e.response?.data?.detail || 'Erreur'))
      .finally(() => setLoadingLocations(false));
  }, [selectedAccount]);

  const save = async () => {
    if (!selectedAccount || !selectedLocation) return;
    const loc = locations.find(l => l.id === selectedLocation);
    setSaving(true);
    try {
      await axios.put(
        `${API}/google-business/clients/${client.id}/link`,
        {
          account_id: selectedAccount,
          location_id: selectedLocation,
          location_title: loc?.title || '',
        },
        { withCredentials: true }
      );
      toast.success(`${client.name} lié à "${loc?.title}"`);
      onLinked?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  const unlink = async () => {
    if (!window.confirm('Supprimer le lien GBP pour ce client ?')) return;
    try {
      await axios.delete(`${API}/google-business/clients/${client.id}/link`, { withCredentials: true });
      toast.success('Lien supprimé');
      onLinked?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="gbp-link-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MapPin className="w-5 h-5 text-[#002FA7]" />
            Lier {client?.name} à Google Business Profile
          </DialogTitle>
        </DialogHeader>

        {notConnected && (
          <div className="flex items-start gap-2 text-xs p-3 rounded bg-amber-50 text-amber-800 border border-amber-200">
            <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <span>Google Business Profile n'est pas connecté. Rendez-vous sur <b>Intégrations & Affiliations</b> pour finaliser la connexion OAuth2.</span>
          </div>
        )}

        {apiError && (
          <div className="flex items-start gap-2 text-xs p-3 rounded bg-red-50 text-red-800 border border-red-200" data-testid="gbp-api-error">
            <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <span>{apiError}</span>
          </div>
        )}

        {!apiError && !notConnected && !loadingAccounts && accounts.length === 0 && (
          <div className="flex items-start gap-2 text-xs p-3 rounded bg-amber-50 text-amber-800 border border-amber-200" data-testid="gbp-no-accounts">
            <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <span>Aucun compte Google Business trouvé. Le compte Google connecté doit être <b>propriétaire ou administrateur</b> d'au moins une fiche d'établissement. Reconnectez GBP depuis Intégrations avec le bon compte Google si besoin.</span>
          </div>
        )}

        {client?.gbp_location_id && (
          <div className="bg-green-50 border border-green-200 rounded p-3 text-sm space-y-1">
            <div className="font-medium text-green-900">Fiche actuellement liée</div>
            <div className="text-xs text-green-800">{client.gbp_location_title || client.gbp_location_id}</div>
          </div>
        )}

        {!notConnected && (
          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium text-neutral-600 uppercase">1. Compte GBP</label>
              <Select value={selectedAccount} onValueChange={setSelectedAccount} disabled={loadingAccounts}>
                <SelectTrigger data-testid="gbp-account-select">
                  <SelectValue placeholder={loadingAccounts ? 'Chargement…' : 'Choisir un compte'} />
                </SelectTrigger>
                <SelectContent>
                  {accounts.map(a => (
                    <SelectItem key={a.id} value={a.id}>{a.displayName} ({a.id})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {selectedAccount && (
              <div>
                <label className="text-xs font-medium text-neutral-600 uppercase">2. Fiche d'établissement</label>
                <Select value={selectedLocation} onValueChange={setSelectedLocation} disabled={loadingLocations}>
                  <SelectTrigger data-testid="gbp-location-select">
                    <SelectValue placeholder={loadingLocations ? 'Chargement…' : (locations.length ? 'Choisir une fiche' : 'Aucune fiche')} />
                  </SelectTrigger>
                  <SelectContent>
                    {locations.map(l => (
                      <SelectItem key={l.id} value={l.id}>
                        {l.title} {l.address ? `· ${l.address}` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        )}

        <DialogFooter className="flex flex-col sm:flex-row gap-2">
          {client?.gbp_location_id && (
            <Button variant="outline" onClick={unlink} className="text-red-600 hover:bg-red-50 hover:text-red-700" data-testid="gbp-unlink-btn">
              <Unlink className="w-4 h-4 mr-1" /> Délier
            </Button>
          )}
          <div className="flex-1" />
          <Button variant="outline" onClick={() => onOpenChange(false)}>Fermer</Button>
          <Button
            onClick={save}
            disabled={!selectedAccount || !selectedLocation || saving || notConnected}
            className="bg-[#002FA7] hover:bg-[#002277]"
            data-testid="gbp-link-save"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Lier'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
